/**
 * The milestones panel, lifted out of the old project dialog unchanged.
 *
 * An empty list here means the project genuinely has no milestones yet, never
 * "the tab is closed": the server strips the whole payload when the grant is
 * off, so the tab is not in `detail.tabs` and this panel is never mounted. That
 * is why the empty line says nothing about permission.
 */
import { CheckCircle2, Circle } from "lucide-react";
import { useT, useI18n } from "../../../../i18n/I18nProvider";
import type { PortalProjectDetail } from "../../../../lib/portal";
import { fmtDate } from "../../../../lib/format";

export function MilestonesPanel({ milestones }: { milestones: PortalProjectDetail["milestones"] }) {
  const t = useT();
  const { locale } = useI18n();

  if (milestones.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t("portal.projects.linkedEmpty")}</p>;
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold">{t("portal.projects.milestonesTitle")}</h4>
      <ul className="space-y-1.5">
        {milestones.map((m) => (
          <li key={m.id} className="flex items-center gap-2 text-sm">
            {m.done
              ? <CheckCircle2 className="size-4 shrink-0 text-success" />
              : <Circle className="size-4 shrink-0 text-muted-foreground" />}
            <span className={m.done ? "text-muted-foreground line-through" : "text-foreground"}>{m.name}</span>
            {m.dueDate && <span className="ml-auto shrink-0 text-xs text-muted-foreground">{fmtDate(m.dueDate, locale)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
